import { useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { uploadToBlob } from '@/api/media-network/mediaNetworkClient';
import { readMediaMetadata } from '@/api/media-network/mediaMetadata';
import { ALLOWED_MEDIA_TYPES, MAX_MEDIA_BYTES } from '@/models/media-network/Playlist';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';

export type UploadPhase = 'idle' | 'reading' | 'uploading' | 'committing';

// The three-step upload handshake, in one place.
//
// Extracted when the library arrived: the same flow now runs from the creative dialog (add a rendition) and
// from a swap (replace one), and it previously existed twice with the steps in slightly different orders.
// Nothing about it is creative-specific — validate, intent, PUT straight to Azure, commit.
//
// The bytes NEVER pass through our API. `uploadUri` points at blob storage, which is why a 250 MB video is
// possible at all.
export function useRenditionUpload() {
    const strings = useLocalizedStrings();

    const [phase, setPhase] = useState<UploadPhase>('idle');
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const busy = phase !== 'idle';

    const phaseLabel =
        phase === 'reading' ? strings.playlistUploadReading
        : phase === 'uploading' ? strings.playlistUploadSending
        : phase === 'committing' ? strings.playlistUploadFinishing
        : '';

    // Returns true on success. `replacesRenditionId` set = a swap: the original stays serveable until the new
    // bytes are confirmed, so a failure anywhere here leaves the creative exactly as it was.
    const upload = async (
        creativeId: string,
        file: File,
        replacesRenditionId?: string,
    ): Promise<boolean> => {
        setError(null);

        // Checked here as a courtesy — the backend re-checks the blob's real type and size at commit, so
        // this is about telling the user early, not about enforcement.
        if (!ALLOWED_MEDIA_TYPES.includes(file.type)) {
            setError(strings.playlistUploadTypeError);
            return false;
        }
        if (file.size > MAX_MEDIA_BYTES) {
            setError(strings.playlistUploadSizeError);
            return false;
        }

        try {
            setPhase('reading');
            setProgress(0);
            // Free: the browser already holds the file. The backend cannot verify these, but it hard-blocks a
            // shape no screen uses, so a wrong-shape file is refused at commit rather than reaching a screen.
            const meta = await readMediaMetadata(file);

            const intent = replacesRenditionId
                ? await mediaNetworkApi.createRenditionReplaceIntent(creativeId, replacesRenditionId, {
                    fileName: file.name, contentType: file.type, sizeBytes: file.size,
                })
                : await mediaNetworkApi.createRenditionIntent(creativeId, {
                    fileName: file.name, contentType: file.type, sizeBytes: file.size,
                });

            setPhase('uploading');
            await uploadToBlob(intent.uploadUri, file, setProgress);

            setPhase('committing');
            await mediaNetworkApi.commitRendition(creativeId, intent.renditionId, {
                width: meta.width ?? null,
                height: meta.height ?? null,
                durationSeconds: meta.durationSeconds ?? null,
            });

            return true;
        } catch (e) {
            // The commit failure worth surfacing verbatim is the shape check — its message names the
            // acceptable shapes, which a generic "upload failed" would throw away.
            setError(errorMessage(e, strings.playlistUploadError));
            return false;
        } finally {
            setPhase('idle');
            setProgress(0);
        }
    };

    return { upload, phase, phaseLabel, progress, busy, error, setError };
}
